'use client';

import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { composeRenderProps } from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';

import type { ClassValue } from 'clsx';

import { CONSTANT } from '@/constants';

/**
 * A helper to merge tailwind classnames intelligently. Combines clsx for conditional
 * class joining with tailwind-merge to handle conflicting classes by keeping the last
 * one. For example:
 *
 * cn('px-2 py-1', isActive && 'px-4') // -> 'py-1 px-4' when isActive is true
 * cn('text-sm text-gray-500', 'text-black') // -> 'text-sm text-black'
 */
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * A helper to create focus ring - extend using tailwind-variants
 */
export const focusRing = tv({
	base: 'outline-0 outline-offset-2 focus:outline-3',
	variants: {
		isFocusVisible: {
			false: 'outline-0',
			true: 'outline-3',
		},
		theme: {
			light: 'outline-light-focus',
			dark: 'outline-dark-focus',
		},
	},
	defaultVariants: {
		theme: 'light',
	},
});

export function composeTailwindRenderProps<T>(
	className: string | ((v: T) => string) | undefined,
	tw: string,
): string | ((v: T) => string) {
	return composeRenderProps(className, className => twMerge(tw, className));
}

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
interface BreakpointConfig {
	min?: number;
	max?: number;
}

const breakpoints: Record<Breakpoint, BreakpointConfig> = {
	xs: { max: CONSTANT.SCREEN_SM_QLDDS - 1 },
	sm: { min: CONSTANT.SCREEN_SM_QLDDS, max: CONSTANT.SCREEN_MD_QLDDS - 1 },
	md: { min: CONSTANT.SCREEN_MD_QLDDS, max: CONSTANT.SCREEN_LG_QLDDS - 1 },
	lg: { min: CONSTANT.SCREEN_LG_QLDDS, max: CONSTANT.SCREEN_XL_QLDDS - 1 },
	xl: { min: CONSTANT.SCREEN_XL_QLDDS, max: CONSTANT.SCREEN_2XL_QLDDS - 1 },
	'2xl': { min: CONSTANT.SCREEN_2XL_QLDDS },
};

/**
 * React hook for responsive design that matches Tailwind's breakpoints
 * @param breakpoint - Target breakpoint to check against - returns true for the given breakpoint and larger screens
 * @param onlyMatchGivenBreakpoint - When true, matches only the specific breakpoint
 * @returns boolean indicating if the current viewport matches the query
 *
 * @example
 * // Matches md and larger screens (≥700px)
 * const isMdScreen = useMediaQuery('md');
 *
 * // Only matches md screens (700px-991px)
 * const isMdScreenOnly = useMediaQuery('md', true);
 */
export const useMediaQuery = (breakpoint: Breakpoint, onlyMatchGivenBreakpoint = false) => {
	const [matches, setMatches] = useState(false);

	const getMediaQuery = (bp: Breakpoint, onlyMatchGivenBreakpoint: boolean): string => {
		const { min, max } = breakpoints[bp];

		if (onlyMatchGivenBreakpoint) {
			if (min && max) {
				return `(min-width: ${min}px) and (max-width: ${max}px)`;
			}
			if (min) {
				// Handles '2xl' when onlyMatchGivenBreakpoint is true
				return `(min-width: ${min}px)`;
			}
			if (max) {
				// Handles 'xs' when onlyMatchGivenBreakpoint is true
				return `(max-width: ${max}px)`;
			}
			// Should not happen with current breakpoints, but return a default
			return '(min-width: 0px)';
		}

		// Default behavior: matches the breakpoint and larger screens
		if (min) {
			return `(min-width: ${min}px)`;
		}

		// For 'xs' when onlyMatchGivenBreakpoint is false, it should match all screens
		return '(min-width: 0px)';
	};

	useEffect(() => {
		const mediaQuery = getMediaQuery(breakpoint, onlyMatchGivenBreakpoint);
		const mediaQueryList = window.matchMedia(mediaQuery);

		const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);

		setMatches(mediaQueryList.matches);
		mediaQueryList.addEventListener('change', onChange);

		return () => mediaQueryList.removeEventListener('change', onChange);
	}, [breakpoint, onlyMatchGivenBreakpoint]);

	return matches;
};
